import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bike, Car, ChevronDown, ExternalLink, Mail, MessageCircle, ShoppingBag, Ship, Sparkles, Users, Bus } from "lucide-react";
import { Pic } from "../Pic";
import { H, POPULAR, COLLECTIONS, FAQ } from "./homeV2I18n";
import { QF_URL, qfRoute, trackFerry } from "../../lib/quickferries";
import { FERRY, FERRY_D } from "../../pages/homeI18n";
import { getConsent } from "../../lib/consent";
import api from "../../lib/api";
import { formatEURL } from "../../lib/format";

export const SITE = "/sito/amalfi-vibes";
const eur = (m, lang = "it") => formatEURL(m, lang);
const svc = (key, lang) => `${SITE}/s/${key}?lang=${lang}`;
const Eyebrow = ({ children, light }) => <p className={`text-[11px] tracking-[0.32em] font-semibold ${light ? "text-[#E9DCC3]" : "text-[#B08A3E]"}`}>{children}</p>;
const Title = ({ children, light, id }) => <h2 id={id} className={`pp-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.06] mt-2 ${light ? "text-white" : "text-[#0B2A4A]"}`}>{children}</h2>;

export const useServices = () => {
  const [m, setM] = useState({});
  useEffect(() => { api.get("/public/pages/amalfi-vibes").then((r) => setM(Object.fromEntries((r.data.services || []).map((s) => [s.key, s])))).catch(() => setM({})); }, []);
  return m;
};

export function priceLines(s, lang) {
  if (!s) return [];
  const p = s.price || {};
  if (p.display !== "show") return [H.onReq[lang]];
  const out = [];
  if (p.price_5h_minor) out.push(`${eur(p.price_5h_minor, lang)} / ${H.h5[lang]}`);
  if (p.price_day_minor) out.push(`${eur(p.price_day_minor, lang)} / ${H.day[lang]}`);
  if (p.price_person_minor) out.push(`${eur(p.price_person_minor, lang)} / ${H.person[lang]}`);
  return out;
}

const QUICK = [
  ["vespa", Bike, H.navExp, (l) => svc("vespa125", l), { en: "Vespa & Scooter", it: "Vespa e Scooter", fr: "Vespa & Scooter", de: "Vespa & Roller", es: "Vespa y Scooter" }],
  ["boats", Ship, null, () => "#boats", { en: "Boat Tours", it: "Tour in barca", fr: "Bateau", de: "Bootstouren", es: "Barcos" }],
  ["car", Car, null, (l) => svc("auto", l), { en: "Car Rentals", it: "Noleggio auto", fr: "Voitures", de: "Autos", es: "Coches" }],
  ["transfer", Bus, null, (l) => svc("transfer", l), { en: "Transfers", it: "Transfer", fr: "Transferts", de: "Transfers", es: "Traslados" }],
  ["shop", ShoppingBag, null, (l) => `/shop?lang=${l}`, { en: "Shop", it: "Shop", fr: "Boutique", de: "Shop", es: "Tienda" }],
];

export function QuickAccess({ lang }) {
  return (
    <nav aria-label={H.quick[lang]} className="relative z-10 -mt-10 sm:-mt-12 px-4 sm:px-10" data-testid="home-quick">
      <ul className="max-w-5xl mx-auto grid grid-cols-5 gap-1.5 sm:gap-3 rounded-[22px] bg-white p-1.5 sm:p-3 shadow-[0_20px_50px_rgba(11,42,74,0.18)] ring-1 ring-[#E9DCC3]">
        {QUICK.map(([id, Icon, , to, label]) => {
          const href = to(lang);
          const cls = "flex flex-col items-center justify-center gap-1.5 min-h-[76px] sm:min-h-[88px] rounded-2xl text-[#0B2A4A] hover:bg-[#F7F3EA] focus-visible:bg-[#F7F3EA] transition-colors px-1 text-center";
          const body = <><Icon size={22} strokeWidth={1.6} className={id === "shop" ? "text-[#B08A3E]" : "text-[#1E7FB0]"} aria-hidden /><span className="text-[11px] sm:text-sm font-semibold leading-tight">{label[lang]}</span></>;
          return <li key={id}>{href.startsWith("#") ? <a href={href} className={cls} data-testid={`home-quick-${id}`}>{body}</a> : <Link to={href} className={cls} data-testid={`home-quick-${id}`}>{body}</Link>}</li>;
        })}
      </ul>
    </nav>
  );
}

function ExpCard({ p, s, lang }) {
  const lines = p.to ? [H.onReq[lang]] : priceLines(s, lang);
  const req = p.to || s?.mode === "request" || s?.price?.display !== "show";
  const to = p.to ? `${p.to}?lang=${lang}` : svc(p.key, lang);
  return (
    <Link to={to} className="group flex sm:flex-col rounded-3xl overflow-hidden bg-white ring-1 ring-[#E9DCC3] hover:shadow-[0_18px_40px_rgba(11,42,74,0.12)] transition-shadow" data-testid={`home-exp-${p.key || "vintage"}`}>
      <span className="relative block w-[38%] shrink-0 sm:w-auto sm:aspect-[4/3] overflow-hidden bg-[#E9DCC3]">{p.raw
        ? <img src={`${p.raw}-400.webp`} srcSet={`${p.raw}-400.webp 400w, ${p.raw}-1080.webp 1080w`} sizes="(min-width: 640px) 33vw, 40vw" alt={p.t[lang]} loading="lazy" decoding="async" width="400" height="300" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        : <Pic base={p.img} alt={p.t[lang]} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}</span>
      <span className="flex flex-col flex-1 p-4 sm:p-5 min-w-0">
        <span className="pp-serif text-xl sm:text-2xl text-[#0B2A4A] leading-tight">{p.t[lang]}</span>
        <span className="text-[13px] sm:text-sm text-[#5A6472] mt-1 sm:mt-1.5">{p.d[lang]}</span>
        <span className="mt-2 sm:mt-3 text-sm sm:text-[15px] font-semibold text-[#0B2A4A]" data-testid={`home-exp-price-${p.key || "vintage"}`}>{lines.length ? lines.join(" · ") : "\u00a0"}</span>
        <span className="mt-3 sm:mt-4 inline-flex items-center justify-center gap-2 h-11 sm:h-12 rounded-full bg-[#0B2A4A] text-white text-[13px] sm:text-sm font-semibold group-hover:bg-[#123a63] transition-colors">{req ? H.ask[lang] : H.check[lang]} <ArrowRight size={15} aria-hidden /></span>
      </span>
    </Link>
  );
}

export function Popular({ lang, services }) {
  return (
    <section id="experiences" className="px-4 sm:px-10 pt-16 sm:pt-24 pb-6 scroll-mt-4" aria-labelledby="home-popular-title" data-testid="home-popular">
      <div className="max-w-7xl mx-auto">
        <Eyebrow>AMALFI VIBES EXPERIENCES</Eyebrow>
        <Title id="home-popular-title">{H.popular[lang]}</Title>
        <p className="text-sm sm:text-base text-[#5A6472] mt-3 max-w-xl">{H.popularSub[lang]}</p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">{POPULAR.map((p) => <ExpCard key={p.key || p.to} p={p} s={services[p.key]} lang={lang} />)}</div>
      </div>
    </section>
  );
}

function BoatCard({ img, title, desc, price, to, icon: Icon, cta, testid }) {
  return (
    <Link to={to} className="group relative flex items-end min-h-[22rem] sm:min-h-[30rem] rounded-3xl overflow-hidden bg-[#0B2A4A]" data-testid={testid}>
      <Pic base={img} sizes="(min-width: 1024px) 50vw, 100vw" alt={title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <span className="absolute inset-0 bg-gradient-to-t from-[#0B2A4A] via-[#0B2A4A]/40 to-transparent" />
      <span className="relative p-6 sm:p-8 text-white">
        <Icon size={20} className="text-[#E9DCC3]" aria-hidden />
        <span className="block pp-serif text-3xl sm:text-4xl mt-2">{title}</span>
        <span className="block text-white/85 text-sm sm:text-base mt-2 max-w-md">{desc}</span>
        <span className="block mt-3 font-semibold" data-testid={`${testid}-price`}>{price}</span>
        <span className="mt-5 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#C9A45C] text-[#0B2A4A] text-sm font-semibold">{cta} <ArrowRight size={15} aria-hidden /></span>
      </span>
    </Link>
  );
}

const FROM = { it: "A partire da", en: "From", fr: "À partir de", de: "Ab", es: "Desde", ru: "От", pt: "A partir de", ko: "최저" };
const PER = { person: { it: "persona", en: "person", fr: "personne", de: "Person", es: "persona", ru: "чел.", pt: "pessoa", ko: "1인" },
  boat: { it: "barca", en: "boat", fr: "bateau", de: "Boot", es: "barco", ru: "лодка", pt: "barco", ko: "보트 1척" } };
const fromTxt = (v, unit, lang) => `${FROM[lang] || FROM.en} ${eur(v, lang)} / ${PER[unit][lang] || PER[unit].en}`;
const SUNSET = { it: "tramonto", en: "sunset", fr: "coucher du soleil", de: "Sonnenuntergang", es: "atardecer", ru: "закат", pt: "pôr do sol", ko: "일몰" };
const OTHER = { it: "altre fasce", en: "other times", fr: "autres créneaux", de: "andere Zeiten", es: "otros horarios", ru: "другое время", pt: "outros horários", ko: "기타 시간대" };

export function Boats({ lang, services }) {
  const sg = services.boat_sg, pv = services.boat_private;
  const sp = sg?.price?.display === "show" ? Object.entries(sg.slot_prices || {}).filter(([, v]) => v > 0) : [];
  const sun = sp.find(([k]) => k === "sunset")?.[1], rest = sp.filter(([k]) => k !== "sunset").map(([, v]) => v);
  const sgMin = sg?.price?.display === "show" ? Math.min(...[sg.price.price_person_minor, ...sp.map(([, v]) => v)].filter((v) => v > 0)) : null;
  const sgp = sun && rest.length && Math.min(...rest) !== sun
    ? `${fromTxt(Math.min(sun, ...rest), "person", lang)} (${SUNSET[lang] || SUNSET.en}) · ${OTHER[lang] || OTHER.en} ${eur(Math.min(...rest), lang)}`
    : sgMin && Number.isFinite(sgMin) ? fromTxt(sgMin, "person", lang) : (sg ? H.onReq[lang] : "");
  const pvFrom = pv?.price?.display !== "hide" ? pv?.content?.from_minor : null;
  const pvp = pvFrom ? fromTxt(pvFrom, "boat", lang) : (pv ? priceLines(pv, lang)[0] : "");
  return (
    <section id="boats" className="px-4 sm:px-10 py-16 sm:py-24 scroll-mt-4" aria-labelledby="home-boats-title" data-testid="home-boats">
      <div className="max-w-7xl mx-auto">
        <Eyebrow>{H.boatsE[lang]}</Eyebrow>
        <Title id="home-boats-title">{H.boatsH[lang]}</Title>
        <div className="mt-8 grid lg:grid-cols-2 gap-4 sm:gap-5">
          <BoatCard img="/hotel/boat-group" title={{ en: "Private tours", it: "Tour privati", fr: "Tours privés", de: "Private Touren", es: "Tours privados" }[lang]} desc={H.bp[lang]} price={pvp} to={svc("boat_private", lang)} icon={Sparkles} cta={H.ask[lang]} testid="home-boats-private" />
          <BoatCard img="/hotel/boat-private" title="Small Group" desc={H.bg[lang]} price={sgp} to={svc("boat_sg", lang)} icon={Users} cta={H.check[lang]} testid="home-boats-group" />
        </div>
      </div>
    </section>
  );
}

export function Collections({ lang }) {
  return (
    <section className="px-4 sm:px-10 py-16 sm:py-24 bg-[#0B2A4A] text-white" aria-labelledby="home-collections-title" data-testid="home-collections">
      <div className="max-w-7xl mx-auto">
        <Eyebrow light>{H.shopE[lang]}</Eyebrow>
        <Title light id="home-collections-title">{H.shopH[lang]}</Title>
        <div className="mt-8 grid sm:grid-cols-3 gap-4 sm:gap-5">
          {COLLECTIONS.map((c) => (
            <Link key={c.id} to={`${c.to}?lang=${lang}`} className="group block rounded-3xl overflow-hidden bg-[#F7F3EA] text-[#0B2A4A]" data-testid={`home-collection-${c.id}`}>
              <span className="relative block aspect-[4/5] overflow-hidden bg-[#E9DCC3]">
                <img src={c.img} alt={c.t} loading="lazy" decoding="async" width="800" height="1000" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                {c.sale && <span className="absolute left-3 top-3 text-[10px] font-semibold tracking-[0.18em] uppercase px-3 py-1.5 rounded-full bg-[#0B2A4A] text-white" data-testid={`home-collection-${c.id}-status`}>{H.onSale[lang]}</span>}
              </span>
              <span className="block p-5">
                <span className="block pp-serif text-2xl">{c.t}</span>
                <span className="block text-sm text-[#5A6472] mt-1">{c.d[lang]}</span>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#B08A3E]">{c.sale ? H.buy[lang] : H.discover[lang]} <ArrowRight size={14} aria-hidden /></span>
              </span>
            </Link>))}
        </div>
      </div>
    </section>
  );
}

const FROM_AMALFI = { it: "Partenze da Amalfi", en: "Departures from Amalfi", fr: "Départs d'Amalfi", de: "Abfahrten ab Amalfi", es: "Salidas desde Amalfi", ru: "Отправления из Амальфи", pt: "Partidas de Amalfi", ko: "아말피 출발" };
const BOOK_NOW = { it: "Prenota ora", en: "Book now", fr: "Réserver", de: "Jetzt buchen", es: "Reservar ahora", ru: "Забронировать", pt: "Reservar agora", ko: "지금 예약" };

export function Ferry({ lang }) {
  const f = FERRY[lang];
  return (
    <section id="ferries" className="px-4 sm:px-10 py-14 sm:py-20 scroll-mt-20" aria-labelledby="home-ferries-title" data-testid="home-ferries">
      <div className="max-w-7xl mx-auto grid md:grid-cols-[1fr_auto] gap-6 items-center rounded-3xl bg-white ring-1 ring-[#E9DCC3] p-6 sm:p-10">
        <div>
          <Eyebrow>QUICKFERRIES</Eyebrow>
          <Title id="home-ferries-title">{H.ferryH[lang]}</Title>
          <p className="text-sm sm:text-base text-[#5A6472] mt-3 max-w-2xl" data-testid="home-ferries-desc">{FERRY_D[lang]}</p>
          <p className="text-xs text-[#5A6472] mt-2">{f.n}</p>
          <p className="text-[11px] font-semibold tracking-[0.2em] text-[#8A6A2E] mt-5 uppercase" data-testid="home-ferries-routes-label">{FROM_AMALFI[lang] || FROM_AMALFI.en}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {[["Positano", 1441], ["Capri", 3707]].map(([d, rid]) => (
              <a key={d} href={qfRoute(rid)} target="_blank" rel="noopener" onClick={() => trackFerry("sito", `amalfi-${d.toLowerCase()}`)} className="inline-flex items-center gap-2 h-11 px-5 rounded-full ring-1 ring-[#0B2A4A]/20 text-[#0B2A4A] text-sm font-semibold hover:bg-[#0B2A4A] hover:text-white transition-colors" data-testid={`home-ferry-route-${d.toLowerCase()}`}>Amalfi → {d} <ExternalLink size={14} aria-hidden /></a>))}
          </div>
        </div>
        <a href={QF_URL} target="_blank" rel="noopener" onClick={() => trackFerry("sito", "amalfi-vibes")} className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-[#C9A45C] text-[#0B2A4A] font-semibold hover:bg-[#E9DCC3] transition-colors" data-testid="home-ferries-book-btn">{BOOK_NOW[lang] || BOOK_NOW.en} <ExternalLink size={16} aria-hidden /></a>
      </div>
    </section>
  );
}

export function FaqHelp({ lang, wa }) {
  const [open, setOpen] = useState(0);
  return (
    <section id="help" className="px-4 sm:px-10 py-16 sm:py-24 scroll-mt-4" aria-labelledby="home-faq-title" data-testid="home-faq">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.4fr_1fr] gap-10">
        <div>
          <Title id="home-faq-title">{H.faqH[lang]}</Title>
          <ul className="mt-6 divide-y divide-[#E9DCC3] border-y border-[#E9DCC3]">
            {FAQ[lang].map(([q, a], i) => (
              <li key={q}>
                <button type="button" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i} aria-controls={`faq-a-${i}`} className="w-full flex items-center justify-between gap-4 py-4 text-left text-[#0B2A4A] font-semibold min-h-[56px]" data-testid={`home-faq-q-${i}`}>
                  {q}<ChevronDown size={18} className={`shrink-0 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} aria-hidden />
                </button>
                <p id={`faq-a-${i}`} hidden={open !== i} className="pb-4 text-sm text-[#5A6472] leading-relaxed" data-testid={`home-faq-a-${i}`}>{a}</p>
              </li>))}
          </ul>
        </div>
        <div className="rounded-3xl bg-[#0B2A4A] text-white p-6 sm:p-8 self-start" data-testid="home-help">
          <p className="pp-serif text-2xl">{H.help[lang]}</p>
          {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="mt-5 flex items-center justify-center gap-2 h-14 rounded-full bg-[#C9A45C] text-[#0B2A4A] font-semibold" data-testid="home-help-whatsapp"><MessageCircle size={18} aria-hidden /> WhatsApp</a>}
          <a href="mailto:info@amalfinewservice.it" className="mt-3 flex items-center justify-center gap-2 h-14 rounded-full border border-white/50 font-semibold hover:bg-white/10 transition-colors" data-testid="home-help-email"><Mail size={18} aria-hidden /> info@amalfinewservice.it</a>
          <Link to={`/legale/contatti?lang=${lang}`} className="mt-4 block text-center text-sm underline text-white/80 py-2" data-testid="home-help-contacts">{{ en: "Contacts & company", it: "Contatti e società", fr: "Contacts et société", de: "Kontakt & Firma", es: "Contactos y empresa" }[lang]}</Link>
        </div>
      </div>
    </section>
  );
}

export function BottomBar({ lang }) {
  const [on, setOn] = useState(false);
  const [banner, setBanner] = useState(() => !getConsent());
  useEffect(() => {
    let raf = 0;
    const f = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => setOn(window.scrollY > 500)); };
    const c = () => setBanner(false);
    const o = () => setBanner(true);
    window.addEventListener("scroll", f, { passive: true });
    window.addEventListener("amalfi-consent", c);
    window.addEventListener("amalfi-consent-open", o);
    f();
    return () => { window.removeEventListener("scroll", f); window.removeEventListener("amalfi-consent", c); window.removeEventListener("amalfi-consent-open", o); cancelAnimationFrame(raf); };
  }, []);
  const show = on && !banner;
  const item = "flex-1 flex flex-col items-center justify-center gap-0.5 h-14 text-[11px] font-semibold";
  return (
    <nav aria-label="Mobile" aria-hidden={!show} className={`md:hidden fixed inset-x-3 z-40 transition-transform duration-500 ${show ? "translate-y-0" : "translate-y-[150%]"}`} style={{ bottom: "calc(env(safe-area-inset-bottom) + 10px)" }} data-testid="home-bottom-bar">
      <div className="flex rounded-full bg-[#0B2A4A]/95 backdrop-blur-md text-white shadow-[0_12px_30px_rgba(11,42,74,0.35)] px-2">
        <a href="#experiences" tabIndex={show ? 0 : -1} className={item} data-testid="home-bottom-exp"><Bike size={18} aria-hidden />{H.navExp[lang]}</a>
        <Link to={`/shop?lang=${lang}`} tabIndex={show ? 0 : -1} className={`${item} text-[#E9DCC3]`} data-testid="home-bottom-shop"><ShoppingBag size={18} aria-hidden />Shop</Link>
        <a href="#help" tabIndex={show ? 0 : -1} className={item} data-testid="home-bottom-help"><MessageCircle size={18} aria-hidden />{H.navHelp[lang]}</a>
      </div>
    </nav>
  );
}
